import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { adminAPI } from '@/lib/api';
import { AppShell } from '@/components/layout/AppShell';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import {
  Trash2, Users, Briefcase, Building2, FileText, Star,
  TreeDeciduous, MessageSquare, Search, AlertTriangle, Shield,
  BarChart3, CheckSquare, Loader2,
} from 'lucide-react';

const TABS = [
  { key: 'users', label: 'Benutzer', icon: Users },
  { key: 'projekte', label: 'Projekte', icon: Briefcase },
  { key: 'profile', label: 'Profile', icon: Building2 },
  { key: 'entwuerfe', label: 'Entwürfe', icon: FileText },
  { key: 'bewertungen', label: 'Bewertungen', icon: Star },
  { key: 'waldflaechen', label: 'Waldflächen', icon: TreeDeciduous },
  { key: 'faelle', label: 'Fälle', icon: BarChart3 },
  { key: 'nachrichten', label: 'Nachrichten', icon: MessageSquare },
];

const DELETE_MAP = {
  users: 'deleteUser', projekte: 'deleteProjekt', profile: 'deleteProfile',
  entwuerfe: 'deleteEntwurf', bewertungen: 'deleteBewertung',
  waldflaechen: 'deleteWaldflaeche', faelle: 'deleteFall', nachrichten: 'deleteNachricht',
};

const AdminPage = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState('users');
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [counts, setCounts] = useState({});
  const [selected, setSelected] = useState(new Set());
  const [deleting, setDeleting] = useState(false);
  const [showBatchConfirm, setShowBatchConfirm] = useState(false);

  const getId = (item) =>
    item.id || item.user_id || item.entwurf_id || item.fall_id || item.profil_id || '';

  const getLabel = (item) => {
    if (activeTab === 'users') return `${item.name || '?'} (${item.email || '?'})`;
    if (activeTab === 'projekte') return `${item.title || item.name || item.id}`;
    if (activeTab === 'profile') return `${item.company_name || item.user_name || item.firma || item.id} – ${item.role || ''}`;
    if (activeTab === 'entwuerfe') return `${(item.freitext || '').slice(0, 80)}...`;
    if (activeTab === 'bewertungen') return `${item.reviewer_name || '?'}: "${(item.kommentar || '').slice(0, 60)}..."`;
    if (activeTab === 'waldflaechen') return `${item.name || item.id} – ${item.flaeche_ha || '?'} ha`;
    if (activeTab === 'faelle') return `${item.fall_typ || '?'} – ${item.fall_id}`;
    if (activeTab === 'nachrichten') return `${item.sender_name || '?'} -> ${item.recipient_name || '?'}: "${(item.content || '').slice(0, 50)}..."`;
    return JSON.stringify(item).slice(0, 80);
  };

  const getMeta = (item) => {
    const parts = [];
    if (item.role) parts.push(item.role);
    if (item.email) parts.push(item.email);
    if (item.bundesland) parts.push(item.bundesland);
    if (item.is_demo) parts.push('Demo');
    if (item.created_at) parts.push(new Date(item.created_at).toLocaleDateString('de-DE'));
    if (item.erstellt_am) parts.push(new Date(item.erstellt_am).toLocaleDateString('de-DE'));
    if (item.erstellt) parts.push(new Date(item.erstellt).toLocaleDateString('de-DE'));
    if (item.rating) parts.push(`${item.rating}/5`);
    return parts.join(' · ');
  };

  const loadData = useCallback(async (tab) => {
    setLoading(true);
    setSelected(new Set());
    setShowBatchConfirm(false);
    try {
      const res = await adminAPI[tab]();
      const items = res.data[tab] || res.data.profile || res.data.faelle || res.data.nachrichten || [];
      setData(items);
    } catch {
      toast.error(t('toasts.adminLadeFehler'));
      setData([]);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadCounts = useCallback(async () => {
    const results = {};
    for (const tab of TABS) {
      try {
        const res = await adminAPI[tab.key]();
        results[tab.key] = res.data.total || 0;
      } catch {
        results[tab.key] = 0;
      }
    }
    setCounts(results);
  }, []);

  useEffect(() => {
    loadData(activeTab);
    loadCounts();
  }, [activeTab, loadData, loadCounts]);

  const filtered = data.filter((item) => {
    if (!search) return true;
    return JSON.stringify(item).toLowerCase().includes(search.toLowerCase());
  });

  const toggleSelect = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
    setShowBatchConfirm(false);
  };

  const toggleSelectAll = () => {
    if (selected.size === filtered.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(filtered.map(item => getId(item)).filter(Boolean)));
    }
    setShowBatchConfirm(false);
  };

  const handleBatchDelete = async () => {
    if (selected.size === 0) return;
    setDeleting(true);
    const deleteFn = DELETE_MAP[activeTab];
    let successCount = 0;
    let failCount = 0;
    for (const id of selected) {
      try {
        await adminAPI[deleteFn](id);
        successCount++;
      } catch {
        failCount++;
      }
    }
    setDeleting(false);
    setShowBatchConfirm(false);
    setSelected(new Set());
    if (successCount > 0) toast.success(`${successCount} ${t('toasts.adminGeloescht')}`);
    if (failCount > 0) toast.error(`${failCount} ${t('toasts.adminLoeschFehler')}`);
    loadData(activeTab);
    loadCounts();
  };

  const allSelected = filtered.length > 0 && selected.size === filtered.length;
  const someSelected = selected.size > 0 && selected.size < filtered.length;

  if (user?.role !== 'admin') {
    return (
      <AppShell>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <Shield className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-gray-900 mb-2">Zugriff verweigert</h2>
            <p className="text-gray-500">Dieser Bereich ist nur für Administratoren.</p>
          </div>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <div className="space-y-6" data-testid="admin-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-serif text-2xl font-bold text-gray-900">Administration</h1>
            <p className="text-sm text-gray-500 mt-1">Alle Daten verwalten und löschen</p>
          </div>
          <Badge className="bg-red-50 text-red-700 border-red-200">
            <Shield className="h-3.5 w-3.5 mr-1" />
            Operator
          </Badge>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-2" data-testid="admin-tabs">
          {TABS.map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => { setActiveTab(key); setSearch(''); }}
              data-testid={`admin-tab-${key}`}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                activeTab === key
                  ? 'bg-primary text-white'
                  : 'bg-white border border-warm-200 text-gray-600 hover:bg-warm-50'
              }`}
            >
              <Icon className="h-4 w-4" />
              {label}
              {counts[key] !== undefined && (
                <span className={`ml-1 px-1.5 py-0.5 rounded-full text-xs ${
                  activeTab === key ? 'bg-white/20' : 'bg-gray-100'
                }`}>
                  {counts[key]}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Search + Batch Actions */}
        <div className="flex items-center gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder={t('placeholders.suchen')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
              data-testid="admin-search"
            />
          </div>
          {selected.size > 0 && (
            <div className="flex items-center gap-2 shrink-0">
              {showBatchConfirm ? (
                <>
                  <span className="text-sm text-red-600 font-medium">{selected.size} löschen?</span>
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={handleBatchDelete}
                    disabled={deleting}
                    data-testid="batch-delete-confirm-btn"
                  >
                    {deleting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Trash2 className="h-4 w-4 mr-1" />}
                    {deleting ? t('toasts.wirdGeloescht') : t('toasts.jaAlleLoeschen')}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setShowBatchConfirm(false)}>
                    Abbrechen
                  </Button>
                </>
              ) : (
                <>
                  <Badge variant="outline" className="text-primary border-primary/30" data-testid="selected-count">
                    <CheckSquare className="h-3 w-3 mr-1" />
                    {selected.size} markiert
                  </Badge>
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={() => setShowBatchConfirm(true)}
                    data-testid="batch-delete-btn"
                  >
                    <Trash2 className="h-4 w-4 mr-1" />
                    Markierte löschen
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setSelected(new Set())}
                    data-testid="clear-selection-btn"
                  >
                    Auswahl aufheben
                  </Button>
                </>
              )}
            </div>
          )}
        </div>

        {/* Data List */}
        <Card>
          <CardContent className="p-0">
            {/* Select All Header */}
            {!loading && filtered.length > 0 && (
              <div className="flex items-center gap-3 px-5 py-2.5 bg-warm-50/80 border-b border-warm-200">
                <Checkbox
                  checked={allSelected}
                  onCheckedChange={toggleSelectAll}
                  data-testid="select-all-checkbox"
                />
                <span className="text-xs font-medium text-gray-500">
                  {allSelected ? 'Alle abwählen' : `Alle ${filtered.length} auswählen`}
                </span>
              </div>
            )}

            {loading ? (
              <div className="p-8 text-center text-gray-400">Laden...</div>
            ) : filtered.length === 0 ? (
              <div className="p-8 text-center text-gray-400">Keine Einträge gefunden</div>
            ) : (
              <div className="divide-y divide-warm-200">
                {filtered.map((item, idx) => {
                  const itemId = getId(item) || `idx-${idx}`;
                  const isSelected = selected.has(itemId);
                  return (
                    <div
                      key={itemId}
                      onClick={() => toggleSelect(itemId)}
                      className={`flex items-center gap-3 px-5 py-3.5 cursor-pointer transition-colors ${
                        isSelected
                          ? 'bg-red-50/60 hover:bg-red-50'
                          : 'hover:bg-warm-50/50'
                      }`}
                      data-testid={`admin-row-${itemId}`}
                    >
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={() => toggleSelect(itemId)}
                        onClick={(e) => e.stopPropagation()}
                        data-testid={`checkbox-${itemId}`}
                      />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-gray-900 truncate">{getLabel(item)}</p>
                        <p className="text-xs text-gray-400 mt-0.5 truncate">{getMeta(item)}</p>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {item.is_demo && <Badge variant="outline" className="text-[10px]">Demo</Badge>}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Warning */}
        <div className="flex items-start gap-3 p-4 rounded-lg bg-amber-50 border border-amber-200">
          <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-amber-800">Achtung: Löschvorgänge sind endgültig</p>
            <p className="text-xs text-amber-600 mt-1">Gelöschte Daten können nicht wiederhergestellt werden. Bei Benutzern werden zugehörige Profile und Waldflächen mitgelöscht.</p>
          </div>
        </div>
      </div>
    </AppShell>
  );
};

export default AdminPage;
